import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogPosts } from '../blogData';
import { Calendar, Clock, Tag, ArrowLeft, Share2, Facebook, Twitter } from 'lucide-react';
import { Button } from './ui/button';
import ReactMarkdown from 'react-markdown';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Add structured data for the blog post
    if (post) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "image": post.image,
        "author": {
          "@type": "Organization",
          "name": post.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "Boss Detailing Pro",
          "logo": {
            "@type": "ImageObject",
            "url": "https://customer-assets.emergentagent.com/job_boss-shine-2026/artifacts/56v5wwxv_boss%20logo.jpg"
          }
        },
        "datePublished": post.date,
        "dateModified": post.date,
        "description": post.metaDescription,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://bossdetailingpros.com/blog/${post.slug}`
        }
      });
      document.head.appendChild(script);
      
      return () => {
        document.head.removeChild(script);
      };
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-black pt-36 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Article Not Found</h1>
          <p className="text-gray-400 mb-8">The article you're looking for doesn't exist.</p>
          <Button
            onClick={() => navigate('/blog')}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  const shareUrl = `https://bossdetailingpros.com/blog/${post.slug}`;

  return (
    <div className="min-h-screen bg-black pt-36 pb-20">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/blog" className="inline-flex items-center text-orange-500 hover:text-orange-400 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        {/* Category Badge */}
        <div className="mb-4">
          <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold">
            {post.category}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-8 pb-8 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            <span>{post.readTime}</span>
          </div>
          <div className="flex items-center gap-2">
            <span>By {post.author}</span>
          </div>
        </div>

        {/* Featured Image */}
        <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-96 object-cover"
          />
        </div>

        {/* Share Buttons */}
        <div className="flex items-center gap-4 mb-8 pb-8 border-b border-zinc-800">
          <span className="text-gray-400 font-medium">Share:</span>
          <button
            onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`, '_blank')}
            className="p-2 bg-zinc-800 hover:bg-blue-600 text-white rounded-full transition-colors"
            aria-label="Share on Facebook"
          >
            <Facebook className="w-5 h-5" />
          </button>
          <button
            onClick={() => window.open(`https://twitter.com/intent/tweet?url=${shareUrl}&text=${post.title}`, '_blank')}
            className="p-2 bg-zinc-800 hover:bg-sky-500 text-white rounded-full transition-colors"
            aria-label="Share on Twitter"
          >
            <Twitter className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigator.clipboard.writeText(shareUrl)}
            className="p-2 bg-zinc-800 hover:bg-orange-500 text-white rounded-full transition-colors"
            aria-label="Copy link"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-lg max-w-none mb-12">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => <h1 className="text-4xl font-bold text-white mb-6 mt-12" {...props} />,
              h2: ({ node, ...props }) => <h2 className="text-3xl font-bold text-white mb-4 mt-10" {...props} />,
              h3: ({ node, ...props }) => <h3 className="text-2xl font-bold text-orange-500 mb-3 mt-8" {...props} />,
              p: ({ node, ...props }) => <p className="text-gray-300 mb-6 leading-relaxed" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2" {...props} />,
              ol: ({ node, ...props }) => <ol className="list-decimal list-inside text-gray-300 mb-6 space-y-2" {...props} />,
              li: ({ node, ...props }) => <li className="text-gray-300" {...props} />,
              strong: ({ node, ...props }) => <strong className="text-white font-bold" {...props} />,
              a: ({ node, ...props }) => <a className="text-orange-500 hover:text-orange-400 underline" {...props} />,
              table: ({ node, ...props }) => (
                <div className="overflow-x-auto mb-6">
                  <table className="min-w-full border border-zinc-700" {...props} />
                </div>
              ),
              th: ({ node, ...props }) => <th className="border border-zinc-700 px-4 py-2 bg-zinc-800 text-white font-bold" {...props} />,
              td: ({ node, ...props }) => <td className="border border-zinc-700 px-4 py-2 text-gray-300" {...props} />,
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-12 pb-12 border-b border-zinc-800">
          {post.tags.map((tag, idx) => (
            <span
              key={idx}
              className="flex items-center gap-1 bg-zinc-800 text-gray-300 px-3 py-1 rounded-full text-sm"
            >
              <Tag className="w-3 h-3" />
              {tag}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-zinc-800 to-zinc-900 rounded-2xl p-8 text-center border border-zinc-700">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Experience Professional Detailing?
          </h3>
          <p className="text-gray-400 mb-6">
            Let Boss Detailing Pro bring showroom shine to your vehicle with mobile service in Sacramento
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 py-4"
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            >
              Get Your Quote
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-orange-500 text-white hover:bg-orange-500 hover:text-white font-bold px-8 py-4"
              onClick={() => window.location.href = 'tel:9162993189'}
            >
              Call (916) 299-3189
            </Button>
          </div>
        </div>

        {/* Related Posts */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-white mb-8">Related Articles</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {blogPosts
              .filter(p => p.id !== post.id && p.category === post.category)
              .slice(0, 2)
              .map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-zinc-800/50 border border-zinc-700 hover:border-orange-500 rounded-xl overflow-hidden transition-all duration-300"
                >
                  <img
                    src={relatedPost.image}
                    alt={relatedPost.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors mb-2">
                      {relatedPost.title}
                    </h4>
                    <p className="text-gray-400 text-sm line-clamp-2">{relatedPost.excerpt}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
